import React from 'react';
import { IntlProvider } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router';
import { ConfigProvider } from 'antd';
import { actions as localizationSliceActions } from '../data/localization/logic';
import type { StoreState } from '../data/store';
import { EventBusProvider } from './event-bus';
import { getThemeConfig, writeDarkMode } from './theme';

interface Props extends React.PropsWithChildren {
  Router?: React.ComponentType;
}
const ContextProvider: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const { Router = BrowserRouter } = props;
  const { messages, locale } = useSelector(
    (s: StoreState) => s['localization']
  );
  const darkMode = useSelector((s: StoreState) => s['session'].darkMode);

  const themeConfig = React.useMemo(() => getThemeConfig(darkMode), [darkMode]);

  React.useEffect(() => {
    dispatch(localizationSliceActions.fetchMessages({ locale }));
  }, [locale]);

  React.useEffect(() => {
    // Static Modal/message/notification calls render outside this tree, so
    // they need the theme handed to them separately.
    ConfigProvider.config({
      holderRender: (children) => (
        <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>
      ),
    });
  }, [themeConfig]);

  React.useEffect(() => {
    writeDarkMode(darkMode);
    // The pre-paint script in config/webpack/html.ts sets this on a cold load;
    // keep it in step when the toggle flips.
    document.documentElement.classList.toggle('dark-mode', darkMode);
  }, [darkMode]);

  return (
    <EventBusProvider>
      <Router>
        <IntlProvider messages={messages} locale={locale} defaultLocale="en">
          <ConfigProvider theme={themeConfig}>{props.children}</ConfigProvider>
        </IntlProvider>
      </Router>
    </EventBusProvider>
  );
};
export default ContextProvider;
